import { MainListLayoutCacheKey, SessionStorage } from './enums/storage';
import * as yup from 'yup';
import moment from 'moment';

import { TimeFormat } from './enums/time-format';
import { TableName } from './enums/table-page';

const tableCacheValidation = (tableName: string) => {
  return yup.object({
    filters: tablesFiltersValidation[tableName],
    generalFilter: yup.string(),
    limit: yup.number().min(0).required(),
    page: yup.number().min(0).required(),
    sort: yup.array().of(yup.object({ id: yup.string(), desc: yup.boolean() })),
  });
};

const tableCacheValidationLoose = () => {
  return yup.object({
    filters: yup.array().of(
      yup.object({
        id: yup.string().required(),
        value: yup.mixed(),
        metadata: yup.object().optional(),
      }),
    ),
    generalFilter: yup.string(),
    limit: yup.number().min(0).required(),
    page: yup.number().min(0).required(),
    sort: yup.array().of(yup.object({ id: yup.string(), desc: yup.boolean() })),
    hiddenColumns: yup.array().of(yup.string()),
    search: yup.string(),
  });
};

const tablesFiltersValidation = {
  [`table_${TableName.Jobs}`]: yup.array().of(
    yup.object({
      id: yup.string().oneOf(['date', 'status', 'supplier', 'profile']),
      value: yup.array().when('id', {
        is: 'status',
        then: (schema) =>
          schema.of(
            yup.object({
              id: yup.string().oneOf(['new', 'on_hold', 'canceled', 'routing', 'routed', 'picked', 'dropped', 'noshow']),
            }),
          ),
        otherwise: (schema) =>
          schema.when('id', {
            is: 'supplier',
            then: (schema) => schema.of(yup.object({ id: yup.string() })),
            otherwise: (schema) =>
              schema.when('id', {
                is: 'profile',
                then: (schema) => schema.of(yup.object({ id: yup.string() })),
                otherwise: (schema) =>
                  schema.when('id', {
                    is: 'date',
                    then: (schema) =>
                      schema.of(
                        yup.string().test('isMoment', '${path} must be a Moment object', function (value) {
                          return moment.isMoment(moment(value, TimeFormat.ServerFormatDateTime));
                        }),
                      ),
                  }),
              }),
          }),
      }),
    }),
  ),
  [`table_${TableName.Trips}`]: yup.array().of(
    yup.object({
      id: yup.string().oneOf(['date', 'status', 'profiles', 'suppliers', 'drivers', 'vehicles', 'productionIds']),
      value: yup.array().when('id', {
        is: 'status',
        then: (schema) =>
          schema.of(
            yup.object({
              id: yup.string().oneOf(['new', 'in-route', 'sent', 'approved', 'done', 'canceled']),
            }),
          ),
        otherwise: (schema) =>
          schema.when('id', {
            is: 'profiles',
            then: (schema) => schema.of(yup.object({ id: yup.string() })),
            otherwise: (schema) =>
              schema.when('id', {
                is: 'suppliers',
                then: (schema) => schema.of(yup.object({ id: yup.string() })),
                otherwise: (schema) =>
                  schema.when('id', {
                    is: 'drivers',
                    then: (schema) => schema.of(yup.object({ id: yup.string() })),
                    otherwise: (schema) =>
                      schema.when('id', {
                        is: 'vehicles',
                        then: (schema) => schema.of(yup.object({ id: yup.string() })),
                        otherwise: (schema) =>
                          schema.when('id', {
                            is: 'productionIds',
                            then: (schema) => schema.of(yup.object({ id: yup.string() })),
                            otherwise: (schema) =>
                              schema.when('id', {
                                is: 'date',
                                then: (schema) =>
                                  schema.of(
                                    yup.string().test('isMoment', '${path} must be a Moment object', function (value) {
                                      return moment.isMoment(moment(value, TimeFormat.ServerFormatDateTime));
                                    }),
                                  ),
                              }),
                          }),
                      }),
                  }),
              }),
          }),
      }),
    }),
  ),
  [`table_${TableName.Drivers}`]: yup.array().of(yup.object({})),
  [`table_${TableName.Users}`]: yup.array().of(yup.object()),
  [`table_${TableName.Shuttles}`]: yup.array().of(yup.object()),
  [`table_${TableName.Cars}`]: yup.array().of(yup.object({})),
  [`table_${TableName.DriverShifts}`]: yup.array().of(
    yup.object({
      id: yup.string().oneOf(['start']),
      value: yup.array().when('id', {
        is: 'start',
        then: (scehma) =>
          scehma.of(
            yup.string().test('isMoment', '${path} must be a Moment object', function (value) {
              return moment.isMoment(moment(value, TimeFormat.ServerFormatDateTime));
            }),
          ),
      }),
    }),
  ),
  [`table_${TableName.Shifts}`]: yup.array().of(yup.object({})),
  [`table_${TableName.Rates}`]: yup.array().of(yup.object({})),
  [`table_${TableName.Locations}`]: yup.array().of(
    yup.object({
      id: yup.string().oneOf(['type']),
      value: yup.array().of(yup.object({ id: yup.string().oneOf(['airport', 'inAndOut', 'school', 'location']) })),
    }),
  ),
  [`table_${TableName.Suppliers}`]: yup.array().of(yup.object({})),
  [`table_${TableName.Services}`]: yup.array().of(yup.object({})),
  [`table_${TableName.Profiles}`]: yup.array().of(yup.object({})),
  [`table_${TableName.Areas}`]: yup.array().of(yup.object({})),
  [`table_${TableName.Sites}`]: yup.array().of(yup.object({})),
  [`table_${TableName.History}`]: yup.array().of(yup.object({})),
  [`table_${TableName.Fares}`]: yup.array().of(yup.object({})),
  [`table_${TableName.ExtraServices}`]: yup.array().of(
    yup.object({
      id: yup.string().oneOf(['profileType']),
      value: yup.array().of(
        yup.object({
          id: yup
            .string()
            .oneOf([
              'individual',
              'retail',
              'wholesale',
              'corporate',
              'website',
              'hotel',
              '3rdParty',
              'tourOperator',
              'receiptiveOperator',
            ]),
        }),
      ),
    }),
  ),
};

const validationSchema = {
  [SessionStorage.Token]: yup.string(),
  [SessionStorage.SystemSpecs]: yup.string(),
  [SessionStorage.SiteId]: yup.string(),
  [SessionStorage.CompanyId]: yup.string(),
  [`table_${TableName.Jobs}`]: tableCacheValidation(`table_${TableName.Jobs}`),
  [`table_${TableName.Shuttles}`]: tableCacheValidation(`table_${TableName.Shuttles}`),
  [`table_${TableName.Trips}`]: tableCacheValidation(`table_${TableName.Trips}`),
  [`table_${TableName.Drivers}`]: tableCacheValidation(`table_${TableName.Drivers}`),
  [`table_${TableName.Cars}`]: tableCacheValidation(`table_${TableName.Cars}`),
  [`table_${TableName.Users}`]: tableCacheValidation(`table_${TableName.Users}`),
  [`table_${TableName.DriverShifts}`]: tableCacheValidation(`table_${TableName.DriverShifts}`),
  [`table_${TableName.Shifts}`]: tableCacheValidation(`table_${TableName.Shifts}`),
  [`table_${TableName.Services}`]: tableCacheValidation(`table_${TableName.Services}`),
  [`table_${TableName.Rates}`]: tableCacheValidation(`table_${TableName.Rates}`),
  [`table_${TableName.Locations}`]: tableCacheValidation(`table_${TableName.Locations}`),
  [`table_${TableName.Suppliers}`]: tableCacheValidation(`table_${TableName.Suppliers}`),
  [`table_${TableName.ExtraServices}`]: tableCacheValidation(`table_${TableName.ExtraServices}`),
  [`table_${TableName.Profiles}`]: tableCacheValidation(`table_${TableName.Profiles}`),
  [`table_${TableName.Areas}`]: tableCacheValidation(`table_${TableName.Areas}`),
  [`table_${TableName.Fares}`]: tableCacheValidation(`table_${TableName.Fares}`),
  [`table_${TableName.Sites}`]: tableCacheValidation(`table_${TableName.Sites}`),
  [MainListLayoutCacheKey.Bookings]: tableCacheValidationLoose(),
  [MainListLayoutCacheKey.Trips]: tableCacheValidationLoose(),
  [MainListLayoutCacheKey.Drivers]: tableCacheValidationLoose(),
  [MainListLayoutCacheKey.Vehicles]: tableCacheValidationLoose(),
  [MainListLayoutCacheKey.DriverShifts]: tableCacheValidationLoose(),
  [MainListLayoutCacheKey.DriverShiftsWeekly]: tableCacheValidationLoose(),
};

const load = async <T>(key: string): Promise<T | null> => {
  let data: unknown;
  const item: string | null = sessionStorage.getItem(key);

  try {
    data = !item ? item : JSON.parse(item);
  } catch (error: unknown) {
    data = item;
  }

  const schema = (validationSchema as Record<string, yup.Schema<unknown> | undefined>)[key];

  if (!schema) {
    return data as T;
  }

  return await schema
    .nullable()
    .validate(data)
    .then((validatedData: unknown) => validatedData as T);
};

const save = (key: string, state: Record<string, unknown> | string): void => {
  if (typeof state === 'object') {
    const serializedState = JSON.stringify(state);

    sessionStorage.setItem(key, serializedState);

    return;
  }

  sessionStorage.setItem(key, state);
};

const sessionState = { load, save };

export default sessionState;
